import { LOCALE_META, type Locale } from './locales.ts';

export type CalendarPref = 'gregory' | 'islamic-umalqura' | 'both';
export type DigitPref = 'latn' | 'native';

export interface FormatPrefs {
  locale: Locale;
  calendar: CalendarPref;
  digits: DigitPref;
  /** Always explicit: formatting never falls back to the machine's zone (spec 11A). */
  timeZone: string;
  timeFormat?: '12h' | '24h';
  dateStyle?: 'short' | 'medium' | 'long';
  /** Append the zone, e.g. "GMT+4". */
  showZone?: boolean;
}

/** Offset labels ("GMT+4") for named zones; plain "UTC" when UTC is the selected zone. */
const zoneNameStyle = (tz: string): 'short' | 'shortOffset' =>
  tz === 'UTC' || tz === 'Etc/UTC' ? 'short' : 'shortOffset';

const DATE_PARTS: Record<'short' | 'medium' | 'long', Intl.DateTimeFormatOptions> = {
  short: { year: 'numeric', month: '2-digit', day: '2-digit' },
  medium: { year: 'numeric', month: 'short', day: 'numeric' },
  long: { weekday: 'long', year: 'numeric', month: 'long', day: 'numeric' },
};

/**
 * BCP 47 tag with Unicode extensions for numbering system and calendar.
 * Example: ar + native digits + Hijri => "ar-u-ca-islamic-umalqura-nu-arab".
 */
export function localeTag(
  prefs: Pick<FormatPrefs, 'locale' | 'digits'>,
  calendar?: 'gregory' | 'islamic-umalqura',
): string {
  const nu = prefs.digits === 'native' ? LOCALE_META[prefs.locale].nativeDigits : 'latn';
  const ext = [calendar ? `ca-${calendar}` : null, `nu-${nu}`].filter(Boolean).join('-');
  return `${prefs.locale}-u-${ext}`;
}

export function formatDate(d: Date, prefs: FormatPrefs, opts?: Intl.DateTimeFormatOptions): string {
  const base = opts ?? DATE_PARTS[prefs.dateStyle ?? 'medium'];
  const fmt = (cal: 'gregory' | 'islamic-umalqura') =>
    new Intl.DateTimeFormat(localeTag(prefs, cal), {
      ...base,
      ...(prefs.showZone && (base.hour !== undefined || base.timeZoneName)
        ? { timeZoneName: zoneNameStyle(prefs.timeZone) }
        : {}),
      timeZone: prefs.timeZone,
    }).format(d);
  if (prefs.calendar === 'both') return `${fmt('gregory')} · ${fmt('islamic-umalqura')}`;
  return fmt(prefs.calendar);
}

export function formatTime(d: Date, prefs: FormatPrefs): string {
  return new Intl.DateTimeFormat(localeTag(prefs), {
    hour: 'numeric',
    minute: '2-digit',
    hourCycle: prefs.timeFormat === '24h' ? 'h23' : 'h12',
    ...(prefs.showZone ? { timeZoneName: zoneNameStyle(prefs.timeZone) } : {}),
    timeZone: prefs.timeZone,
  }).format(d);
}

export function formatDateTime(d: Date, prefs: FormatPrefs): string {
  const datePart = DATE_PARTS[prefs.dateStyle ?? 'medium'];
  const opts: Intl.DateTimeFormatOptions = {
    ...datePart,
    hour: 'numeric',
    minute: '2-digit',
    hourCycle: prefs.timeFormat === '24h' ? 'h23' : 'h12',
  };
  return formatDate(d, prefs, opts);
}

export function formatNumber(
  n: number,
  prefs: Pick<FormatPrefs, 'locale' | 'digits'>,
  opts?: Intl.NumberFormatOptions,
) {
  return new Intl.NumberFormat(localeTag(prefs), opts).format(n);
}

/** Minor-unit exponent per ISO 4217. KWD, BHD, OMR (and JOD) use 3 decimals. */
export const CURRENCY_DECIMALS: Record<string, number> = {
  AED: 2,
  SAR: 2,
  QAR: 2,
  KWD: 3,
  BHD: 3,
  OMR: 3,
  INR: 2,
  USD: 2,
};

/** Formats an amount held in minor units (integer, e.g. fils/baisa/paise) to avoid floating point money. */
export function formatMoney(
  minor: bigint | number,
  currency: string,
  prefs: Pick<FormatPrefs, 'locale' | 'digits'>,
): string {
  const exp = CURRENCY_DECIMALS[currency] ?? 2;
  const value = Number(minor) / 10 ** exp;
  return new Intl.NumberFormat(localeTag(prefs), {
    style: 'currency',
    currency,
    minimumFractionDigits: exp,
    maximumFractionDigits: exp,
  }).format(value);
}

export function formatRelative(from: Date, to: Date, prefs: Pick<FormatPrefs, 'locale' | 'digits'>): string {
  const rtf = new Intl.RelativeTimeFormat(localeTag(prefs), { numeric: 'auto' });
  const s = Math.round((from.getTime() - to.getTime()) / 1000);
  const abs = Math.abs(s);
  if (abs < 60) return rtf.format(s, 'second');
  if (abs < 3600) return rtf.format(Math.round(s / 60), 'minute');
  if (abs < 86400) return rtf.format(Math.round(s / 3600), 'hour');
  return rtf.format(Math.round(s / 86400), 'day');
}
